import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from 'reactstrap';
import { Dialog, DialogContent, DialogTitle} from "@mui/material";

import { StatusService } from '../../services/StatusService';

import Toast from "../../helpers/Toast";

import ButtonComponent from '../../components/ButtonComponent';
import TableComponent from "../../components/TableComponent";
import { GetPossibleStatusByBaseStatus } from "../../models/Status/GetPossibleStatusByBaseStatusDTO";
import CreateNextStatusPermission from './CreateNextStatusPermissions';
import { RolePossibleStatusPermissionDTO } from "../../models/Status/RolePossibleStatusPermissionDTO";
import { NextPossibleStatusDTO } from '../../models/Status/NextPossibleStatusDTO';

type NextPossibleStatusProps = {
    BaseStatus: string;
    NextStatus: string;
    nextStatusName: string;
    onSuccess: () => void;
    onCancel: () => void;
}

function NextStatusPermissions(props: NextPossibleStatusProps){
    const statusService = new StatusService();
    const intl = useIntl();

    const[nextPossibleStatus, setNextPossibleStatus] = useState<NextPossibleStatusDTO>({} as NextPossibleStatusDTO);

    const [role, setRole] = useState<RolePossibleStatusPermissionDTO[]>([]);
    const [pageTitle, setPageTitle] = useState("Perfis permitidos a alterar do estado base para próximo estado");

    const pageSizes: number[] = [5, 15, 30];
    const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [isCreatePermissionModalOpen, setIsCreatePermissionModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteParams, setDeleteParams] = useState<{ nextPossibleStatusId : number; roleId: string } | null>(null);

    const columns = React.useMemo(
                () => [
                  {     
                    Header: <FormattedMessage id="Screens.User.Info.Role" />,
                    accessor: "roleName",
                    width: 70,
                  },
                  {
                        Header: "", 
                        accessor: "actions",
                        Cell: ({ row }: any) => ( 
                            <ButtonComponent
                            text={intl.formatMessage({ id: "General.Delete" })}
                            onClick={() => handleDialogDelete(row.original.nextStatusId, row.original.roleId)}
                            />
                        ),
                    },
                ],
              []
            );

    const GetNextStatusRole = async (currentPage: number, pageSize: number) => {
        setIsLoading(true);
    
        const possiblePermissionId = await statusService.GetPossibleStatusById(
            Number(props.BaseStatus), 
            Number(props.NextStatus)
        );
    
        if (!possiblePermissionId.obj) {
            setIsLoading(false);
            return;
        }

        if (nextPossibleStatus.id !== possiblePermissionId.obj.id) {
            setNextPossibleStatus(possiblePermissionId.obj);
        }
    
        const data: GetPossibleStatusByBaseStatus = {
            id: String(possiblePermissionId.obj.id), 
            currentPage,
            pageSize,
        };
    
        const role = await statusService.GetPermissionToEditStatus(data);
    
        if (!role.success || !role.items) {
            Toast.Show("error", "Não foi possível encontrar os perfis permitidos.");
            setRole([]);
            setPageCount(0);
            setPageSize(0);
            setCurrentPage(0);
            setGoToPage(0);
            setIsLoading(false);
            return;
        }
    
        if (role.success && role.items.length == 0) {
            setRole([]);
            setPageCount(0);
            setPageSize(0);
            setCurrentPage(0);
            setGoToPage(0);
            setIsLoading(false);
            return;
        }

        setRole(role.items);
        setPageCount(role.totalPages);
        setPageSize(pageSize);
        setCurrentPage(currentPage);
        setGoToPage(0);
        setIsLoading(false);
    };
    

    useEffect(() => {
    if(props.nextStatusName !== "" || undefined){
        GetNextStatusRole(currentPage ?? 1, pageSize ?? 5);
        
        setPageTitle(`Perfis permitidos a alterar do estado base para próximo estado: ${props.nextStatusName}`);
    }
    }, [props.nextStatusName, currentPage, pageSize]);

    const onNewTaskClick = () => {
        setIsCreatePermissionModalOpen(true);
    };

    const refreshList = () => {
        GetNextStatusRole(currentPage, pageSize);
    };

    const handleDialogDelete = (nextPossibleStatusId : number, roleId: string) => {
        if (!roleId || roleId.trim() === "") {
            Toast.Show("error", "ID do perfil está ausente ou inválido.");
            return;
        }

        setDeleteParams({ nextPossibleStatusId , roleId });
        setConfirmDelete(true);
    };

    const handleCancelDelete = () => {
        setConfirmDelete(false);
        setDeleteParams(null);
    };

    const handleConfirmDelete = async () => {
        if (deleteParams) {
            const result = await statusService.DeletePermissionFromStatusId(deleteParams.nextPossibleStatusId, deleteParams.roleId);

            if (result.success) {
                Toast.Show("success", result.message ?? "Permissão apagada com sucesso.");
                GetNextStatusRole(currentPage, pageSize);
            } else {
                Toast.Show("error", result.message ?? "Erro ao apagar a permissão.");
            }
        }
        setConfirmDelete(false);
        setDeleteParams(null);
    };

    return(
        <>
        <Row>
            <Col>
                <Row style={{ marginBottom: '10px' }}>
                    <Col xs="auto">
                        <ButtonComponent
                        text={intl.formatMessage({ id: "Screens.Status.NextStatusPermissions.Back" })}
                        onClick={props.onCancel}
                        style={{ minHeight: "37px", minWidth: "120px" }}
                        />
                    </Col>
                    <Col>
                        <ButtonComponent
                        text={intl.formatMessage({ id: "Screens.Status.Create.Permission" })}
                        onClick={onNewTaskClick}
                        style={{ minHeight: "37px", minWidth: "120px" }}
                        />
                    </Col>
                </Row>
                <TableComponent
                    columns={columns}
                    data={role}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={pageCount}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoading}
                    fetchData={GetNextStatusRole}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
            </Col>
            <Dialog
                open={isCreatePermissionModalOpen}
                onClose={() => setIsCreatePermissionModalOpen(false)}
                fullWidth
                maxWidth="sm"
                >
                <DialogTitle>Criar Permissão</DialogTitle>
                <DialogContent>
                    <CreateNextStatusPermission
                    BaseStatus={nextPossibleStatus.id}
                    nextStatus={props.NextStatus}
                    baseStatusName={props.nextStatusName}
                    onSuccess={() => {
                        setIsCreatePermissionModalOpen(false);
                        refreshList();
                    }}
                    onCancel={() => setIsCreatePermissionModalOpen(false)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={confirmDelete}
                onClose={handleCancelDelete}
                fullWidth
                >
                <DialogTitle>
                    {intl.formatMessage({ id:  "Screens.Status.ConfirmDelete.Title"})}
                </DialogTitle>
                <DialogContent>
                    <p>{intl.formatMessage({ id: "Screens.Status.Confirm.Reject"})}</p>
                </DialogContent>
                <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px" }}>
                    <ButtonComponent
                        text={intl.formatMessage({ id: "Common.Buttons.Cancel"})}
                        onClick={handleCancelDelete}
                        style={{ marginRight: "8px" }}
                    />
                    <ButtonComponent
                        text={intl.formatMessage({ id: "General.Delete"})}
                        onClick={handleConfirmDelete}
                    />
                </div>
            </Dialog>
        </Row>
        </>
    )
}

export default NextStatusPermissions;